import React, { useRef, useEffect } from 'react';
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import TaskItem from './TaskItem';
import BreakdownForm from './BreakdownForm';

export default function SubEpicTaskList({
  modalChildTasks,
  editingTaskId,
  setEditingTaskId,
  setEditingTaskField,
  users,
  allStories,
  handleUpdateTaskField,
  handleCreateStoryAndAssign,
  handleTaskDragEnd,
  selectedTaskIndex,
  highlightedTaskId,
  // BreakdownForm props
  showBreakdownOptions,
  setShowBreakdownOptions,
  modalChild,
  breakdownNewStoryName,
  setBreakdownNewStoryName,
  breakdownTaskEstimate,
  setBreakdownTaskEstimate,
  breakdownTaskStatus,
  setBreakdownTaskStatus,
  breakdownTaskAssignee,
  setBreakdownTaskAssignee,
  breakdownSelectedStoryId,
  setBreakdownSelectedStoryId,
  breakdownStories,
  storyInputValue,
  setStoryInputValue,
  noStorySelected,
  setNoStorySelected,
  handleCreateNewTask,
  breakdownOptionsRef
}) {
  const droppableRef = useRef(null);
  const isDraggingRef = useRef(false);
  
  // Check which fields are populated across all tasks
  const hasEstimateInList = modalChildTasks.some(t => t.estimate);
  const hasStatusInList = modalChildTasks.some(t => t.status);
  const hasAssigneeInList = modalChildTasks.some(t => t.owner_id);
  const hasStoryInList = modalChildTasks.some(t => t.story_id);

  // Handle drag start to track when dragging begins
  const handleDragStart = () => {
    isDraggingRef.current = true;
  };

  // Handle drag update to help library recalculate positions during scroll
  const handleDragUpdate = (update) => {
    // The library should handle scroll automatically, but we track updates
    // to help with any edge cases
  };

  // Handle drag end to clean up
  const handleDragEnd = (result) => {
    isDraggingRef.current = false;
    handleTaskDragEnd(result);
  };

  // Listen for scroll events during drag to help library recalculate
  useEffect(() => {
    const handleScroll = () => {
      if (isDraggingRef.current && droppableRef.current) {
        // The library should handle scroll automatically
        // We just track that scrolling is happening during drag
        // The DragDropContext will recalculate positions
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div 
      style={{ 
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 4,
        paddingLeft: 8,
        paddingRight: 8,
        marginBottom: 8
      }}
      onClick={(e) => {
        // If clicking outside any task (on the container itself), exit edit mode
        if (e.target === e.currentTarget || (!e.target.closest('[data-task-id]') && !e.target.closest('[data-breakdown-form]'))) {
          if (editingTaskId !== null) {
            setEditingTaskId(null);
            setEditingTaskField(null);
          }
        }
      }}
    >
      <DragDropContext 
        onDragStart={handleDragStart}
        onDragUpdate={handleDragUpdate}
        onDragEnd={handleDragEnd}
        getContainerForClone={() => document.body}
      >
        <Droppable droppableId="tasks-list">
          {(provided) => {
            // Store the ref for scroll compensation
            const combinedRef = (node) => {
              provided.innerRef(node);
              droppableRef.current = node;
            };
            
            return (
            <div 
              {...provided.droppableProps} 
              ref={combinedRef} 
              style={{ display: 'flex', flexDirection: 'column' }}
              onClick={(e) => {
                // If clicking outside any task (on the container itself), exit edit mode
                if (e.target === e.currentTarget || (!e.target.closest('[data-task-id]') && !e.target.closest('[data-breakdown-form]'))) {
                  if (editingTaskId !== null) {
                    setEditingTaskId(null);
                    setEditingTaskField(null);
                  }
                }
              }}
            >
              {modalChildTasks.map((task, index) => {
                const storyTitle = task.storyTitle || '';
                const displayStoryTitle = storyTitle.length > 30 
                  ? storyTitle.substring(0, 30) + '...' 
                  : storyTitle;
                const isEditing = editingTaskId === task.id;
                const owner = users.find(u => u.id === task.owner_id);
                const ownerName = owner 
                  ? (!owner.lastname ? owner.firstname : owner.firstname + " " + owner.lastname.charAt(0))
                  : null;
                const isSelected = selectedTaskIndex === index;
                const isHighlighted = highlightedTaskId === task.id;
                
                return (
                  <TaskItem
                    key={task.id}
                    task={task}
                    index={index}
                    storyTitle={displayStoryTitle}
                    isEditing={isEditing}
                    owner={owner}
                    ownerName={ownerName}
                    users={users}
                    onUpdateTaskField={handleUpdateTaskField}
                    onCreateStoryAndAssign={handleCreateStoryAndAssign}
                    onSetEditingTaskId={setEditingTaskId}
                    onSetEditingTaskField={setEditingTaskField}
                    stories={allStories}
                    hasEstimateInList={hasEstimateInList}
                    hasStatusInList={hasStatusInList}
                    hasAssigneeInList={hasAssigneeInList}
                    hasStoryInList={hasStoryInList}
                    isSelected={isSelected}
                    isHighlighted={isHighlighted}
                  />
                );
              })}
              {provided.placeholder}
              <BreakdownForm
                showBreakdownOptions={showBreakdownOptions}
                setShowBreakdownOptions={setShowBreakdownOptions}
                modalChild={modalChild}
                breakdownNewStoryName={breakdownNewStoryName}
                setBreakdownNewStoryName={setBreakdownNewStoryName}
                breakdownTaskEstimate={breakdownTaskEstimate}
                setBreakdownTaskEstimate={setBreakdownTaskEstimate}
                breakdownTaskStatus={breakdownTaskStatus}
                setBreakdownTaskStatus={setBreakdownTaskStatus}
                breakdownTaskAssignee={breakdownTaskAssignee}
                setBreakdownTaskAssignee={setBreakdownTaskAssignee}
                breakdownSelectedStoryId={breakdownSelectedStoryId}
                setBreakdownSelectedStoryId={setBreakdownSelectedStoryId}
                breakdownStories={breakdownStories}
                storyInputValue={storyInputValue}
                setStoryInputValue={setStoryInputValue}
                noStorySelected={noStorySelected}
                setNoStorySelected={setNoStorySelected}
                users={users}
                handleCreateNewTask={handleCreateNewTask}
                breakdownOptionsRef={breakdownOptionsRef}
                hasEstimateInList={hasEstimateInList}
                hasStatusInList={hasStatusInList}
                hasAssigneeInList={hasAssigneeInList}
                hasStoryInList={hasStoryInList}
              />
            </div>
            );
          }}
        </Droppable>
      </DragDropContext>
    </div>
  );
}

